"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.isResizableCdnUrl = exports.buildResizedCdnUrl = exports.buildCdnSrcSet = exports.buildBackgroundImageValue = exports.buildAvatarSrcSet = exports.DEFAULT_WIDTHS = void 0;

var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));

/**
 * Centralized helpers for building resized CDN image URLs.
 *
 * Three call sites used to roll their own variants of "rewrite a CDN URL to
 * ask for a resized variant":
 *   - LazyImage rewrote `/attachment/` URLs to `/attachment/height/<n>/...`
 *     post-mount, after measuring the rendered element.
 *   - UserAvatar's `transformAvatarImageUrlSize` rewrote `/icon/<size>/...`
 *     URLs and hand-rolled a 2x/3x `srcSet`.
 *   - TopicContent's `getImageUrl` rewrote attachment paths to
 *     `/attachment/width/<n>/...` with its own gif passthrough and CDN-prefix
 *     handling.
 *
 * They had subtly different rules (gif handling vs none, width-based vs
 * height-based, with or without an avatar host allow-list). This module is
 * the single source of truth — every other variant should be migrated to call
 * these helpers.
 *
 * Resizability is decided by URL shape, not host: the `/attachment/` path
 * segment for content images and the `/icon/` segment for avatars. The path
 * shape is defined by our backend (`Cdn::uploadCdn` in PHP) regardless of
 * which CDN host fronts it, so a host allow-list would just lag behind every
 * new whitelabel CDN endpoint without adding safety. Callers are responsible
 * for not feeding in third-party URLs that happen to contain `/attachment/`.
 */
var ATTACHMENT_PATH_SEGMENT = '/attachment/';
var ICON_PATH_SEGMENT = '/icon/';
var ALREADY_RESIZED_PATTERN = /\/attachment\/(width|height)\//;

var hasGifExtension = function hasGifExtension(src) {
  return src.toLowerCase().endsWith('.gif');
};
/**
 * Returns true when `src` points at a CDN attachment that the CDN can resize
 * for us. Specifically:
 *   - the URL contains the `/attachment/` path segment,
 *   - the URL is not already resized (`/attachment/width/...` or
 *     `/attachment/height/...`),
 *   - and the URL does not point at a `.gif` (the CDN cannot resize gifs).
 */


var isResizableCdnUrl = function isResizableCdnUrl(src) {
  if (typeof src !== 'string' || src === '') return false;
  if (hasGifExtension(src)) return false;
  if (ALREADY_RESIZED_PATTERN.test(src)) return false;
  return src.includes(ATTACHMENT_PATH_SEGMENT);
};

exports.isResizableCdnUrl = isResizableCdnUrl;

/**
 * Rewrites a resizable CDN URL to ask the CDN for a width- or height-bounded
 * variant. Pass-through when `src` isn't a resizable CDN URL (gif, already
 * resized, no `/attachment/` segment) or when neither dimension is provided.
 *
 * If both `width` and `height` are supplied, `width` wins — the underlying
 * URL scheme only supports one dimension at a time.
 */
var buildResizedCdnUrl = function buildResizedCdnUrl(src, _ref) {
  var width = _ref.width,
      height = _ref.height;
  if (!isResizableCdnUrl(src)) return src;

  if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
    return src.replace(ATTACHMENT_PATH_SEGMENT, "".concat(ATTACHMENT_PATH_SEGMENT, "width/").concat(Math.round(width), "/"));
  }

  if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
    return src.replace(ATTACHMENT_PATH_SEGMENT, "".concat(ATTACHMENT_PATH_SEGMENT, "height/").concat(Math.round(height), "/"));
  }

  return src;
};
/**
 * Builds a `srcset` value advertising one variant per width in `widths` —
 * e.g. `"…/width/240/foo 240w, …/width/480/foo 480w"`.
 *
 * Returns an empty string when `src` isn't a resizable CDN URL or when no
 * widths are provided. Callers should treat an empty result as "don't set
 * `srcSet` on the element".
 */


exports.buildResizedCdnUrl = buildResizedCdnUrl;

var buildCdnSrcSet = function buildCdnSrcSet(src, widths) {
  if (!isResizableCdnUrl(src) || widths.length === 0) return '';
  return widths.filter(function (w) {
    return Number.isFinite(w) && w > 0;
  }).map(function (w) {
    return "".concat(buildResizedCdnUrl(src, {
      width: w
    }), " ").concat(Math.round(w), "w");
  }).join(', ');
};
/**
 * Callers that know their exact slot (e.g. narrow card thumbnails) should pass
 * an explicit `widths` array to avoid fetching unnecessarily large variants.
 */


exports.buildCdnSrcSet = buildCdnSrcSet;
var DEFAULT_WIDTHS = [360, 480, 720, 960, 1080, 1440, 1920, 2160];
/**
 * Default 1x base width for the `-webkit-image-set()` value emitted by
 * `buildBackgroundImageValue`. Picked to sit in the middle of `DEFAULT_WIDTHS`
 * so the 2x variant (1440) lands on a descriptor already in the ladder —
 * foregrounds and backgrounds share the same upper byte cap.
 *
 * Callers with smaller slots (icon-sized cards, badge thumbnails) should
 * pass an explicit `baseWidth` to `buildBackgroundImageValue` to avoid
 * over-fetching.
 */

exports.DEFAULT_WIDTHS = DEFAULT_WIDTHS;
var DEFAULT_BACKGROUND_BASE_WIDTH = 720;
/**
 * Builds a CSS `background-image` value that uses `-webkit-image-set()` to
 * give the browser DPR-aware variants — 1x at `baseWidth`, 2x at
 * `baseWidth * 2`. Pass-through (single `url("…")`) for non-resizable CDN
 * URLs and for non-positive `baseWidth`.
 *
 * Returns just the value, e.g. `-webkit-image-set(url("…/width/720/foo") 1x,
 * …)`. Callers slot it straight into the `backgroundImage` style key.
 *
 * Browser support — why we use the `-webkit-` prefix:
 *
 *   - `-webkit-image-set()` works in Chrome 21+, Edge 79+, Safari 6+, and
 *     Firefox 89+ (Firefox aliases `-webkit-image-set` to its unprefixed
 *     form for compatibility).
 *   - The unprefixed `image-set()` only landed in Chrome/Edge 113 (May
 *     2023) and Safari 17.0 (Sept 2023). Using the prefix gives strictly
 *     broader real-world coverage, especially for iOS users stuck on
 *     iOS 14–16.x (devices like iPhone 6s, 7, original SE).
 *
 * IE 11 supports neither form and will render no background — an accepted
 * trade-off since these images are decorative. Once PR 4 migrates
 * background callers to `<img>` with `object-fit: cover`, the standard
 * `srcset` algorithm handles DPR + responsive slot together and this
 * helper becomes obsolete.
 */

var buildBackgroundImageValue = function buildBackgroundImageValue(src) {
  var baseWidth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_BACKGROUND_BASE_WIDTH;
  if (!isResizableCdnUrl(src)) return "url(\"".concat(src, "\")");
  if (!Number.isFinite(baseWidth) || baseWidth <= 0) return "url(\"".concat(src, "\")");
  var url1x = buildResizedCdnUrl(src, {
    width: baseWidth
  });
  var url2x = buildResizedCdnUrl(src, {
    width: baseWidth * 2
  });
  return "-webkit-image-set(url(\"".concat(url1x, "\") 1x, url(\"").concat(url2x, "\") 2x)");
};
/**
 * Avatar URLs follow `{cdn}/{community}/icon/{size}/{filename}` (sometimes
 * with a nested original-size segment, e.g. `…/icon/40x40/{IMAGE_SIZE}/foo`).
 * Per-size variants are produced by replacing whatever sits between
 * `/icon/` and the final filename with the requested size.
 */


exports.buildBackgroundImageValue = buildBackgroundImageValue;

var isResizableAvatarUrl = function isResizableAvatarUrl(src) {
  if (typeof src !== 'string' || src === '') return false;
  return src.includes(ICON_PATH_SEGMENT) || src.includes(ATTACHMENT_PATH_SEGMENT) && !ALREADY_RESIZED_PATTERN.test(src);
};

var transformAvatarSize = function transformAvatarSize(src, newSize) {
  var currentPath = src.includes(ICON_PATH_SEGMENT) ? ICON_PATH_SEGMENT : ATTACHMENT_PATH_SEGMENT;

  var _src$split = src.split(currentPath),
      _src$split2 = (0, _slicedToArray2.default)(_src$split, 1),
      baseUrl = _src$split2[0];

  var filename = src.substring(src.lastIndexOf('/') + 1);
  return "".concat(baseUrl).concat(currentPath).concat(newSize, "/").concat(filename);
};

/**
 * Builds a `{ src, srcSet }` pair for a square avatar at `baseSize`, with
 * 2x and 3x retina variants in `srcSet`. Returns the original URL untouched
 * when it's not a recognisable resizable avatar URL — callers can pass the
 * result straight through to `<img>` without further checks.
 *
 * Note: any segment between `/icon/` and the filename is replaced wholesale.
 * For a nested URL like `…/icon/40x40/64x64/foo.png`, the inner `64x64` is
 * dropped from the result — matching the behavior of the original avatar
 * resize helper.
 */
var buildAvatarSrcSet = function buildAvatarSrcSet(src, baseSize) {
  if (!isResizableAvatarUrl(src)) {
    return {
      src: src
    };
  }

  var _baseSize$split = baseSize.split('x'),
      _baseSize$split2 = (0, _slicedToArray2.default)(_baseSize$split, 1),
      baseDimRaw = _baseSize$split2[0];

  var baseDim = Number(baseDimRaw);

  if (!Number.isFinite(baseDim) || baseDim <= 0) {
    return {
      src: src
    };
  }

  var stdSize = "".concat(baseDim, "x").concat(baseDim);
  var x2Size = "".concat(baseDim * 2, "x").concat(baseDim * 2);
  var x3Size = "".concat(baseDim * 3, "x").concat(baseDim * 3);
  return {
    src: src,
    srcSet: "".concat(transformAvatarSize(src, stdSize), " x, ").concat(transformAvatarSize(src, x2Size), " 2x, ").concat(transformAvatarSize(src, x3Size), " 3x")
  };
};

exports.buildAvatarSrcSet = buildAvatarSrcSet;